import type { OptimizelyUserContext } from '@optimizely/optimizely-sdk';
import type { Event, EventName, Plugin } from '../../types';
import type { EventName as OptimizelyAllowedEventNames } from 'lib/sdks/optimizely/optimizely.hook';
import { EventName as OptimizelySpecificEventNames } from 'lib/sdks/optimizely/optimizely.hook';
import { debugLog } from 'lib/utils/debug';
import { formatEventData } from '../../formatter';
import { QueueOfFunctions } from '../../queue';

interface OptimizelyEvent {
  id: string;
  key: string;
}

export interface OptimizelyDatafile {
  events: OptimizelyEvent[];
}

// from https://github.com/optimizely/javascript-sdk/pull/957/files
type EventPropertyValue = string | number | boolean;

type EventProperties = {
  $opt_event_properties?: {
    [key: string]: EventPropertyValue;
  };
};

type EventTags =
  | {
      [key: string]: string | number | null;
    }
  | EventProperties;

const queueUntilOptimizelySetup = new QueueOfFunctions();
let optimizelyUser: OptimizelyUserContext;
const optimizelyAllowedEvents: string[] = [];

const trackConversionEvents = (event: Event) => {
  const source_event: string = event.name;
  let convert_type: string;
  let job_id: string | null = null;

  switch (event.name) {
    case 'job.sent':
      convert_type = event?.customData?.type;
      job_id = event?.customData?.job_id;
      break;
    case 'job.submitted':
      convert_type = event?.customData?.type;
      break;
    case 'global.call_trader':
      convert_type = 'call_trader';
      break;
    case 'global.reveal_phone_number':
      convert_type = 'reveal_phone_number';
      break;
    default:
      // no events match exit function early.
      return;
  }

  trackEventInOptimizely(OptimizelySpecificEventNames.CONTACT_CONVERSION, {
    $opt_event_properties: {
      convert_type,
      job_id: job_id ?? '',
      source_event,
    },
  });
};

export const setOptimizelyUserForTracking = (
  optimizelyUserToSet: OptimizelyUserContext,
  optimizelyDatafile: OptimizelyDatafile,
) => {
  optimizelyUser = optimizelyUserToSet;

  const events: OptimizelyEvent[] = optimizelyDatafile.events;

  events.forEach(event => optimizelyAllowedEvents.push(event.key));

  queueUntilOptimizelySetup.setAsReady();
};

export const formatEventName = (name: string) => {
  // Convert our format to Optimizely standard https://docs.developers.optimizely.com/feature-experimentation/docs/create-events
  return name.toLowerCase().replaceAll(/\W+/g, '_');
};

const getEventTagsFromEvent = (event: Event): EventTags => {
  const formattedEvent = formatEventData(event);

  const formattedEventFlattened = {
    ...formattedEvent,
    ...formattedEvent?.extra_params,
  };

  for (const key in formattedEventFlattened) {
    const typedKey = key as keyof typeof formattedEventFlattened;
    const value = formattedEventFlattened[typedKey];

    switch (true) {
      case value === undefined:
        delete formattedEventFlattened[typedKey];
        break;
      case Array.isArray(value):
        (formattedEventFlattened[typedKey] as string) = (value as any[]).join(
          ',',
        );
        break;
      case !['number', 'string', 'boolean'].includes(typeof value):
        (formattedEventFlattened[typedKey] as string) = JSON.stringify(value);
        break;
      default:
        break;
    }
  }

  const eventTags = {
    // https://docs.developers.optimizely.com/feature-experimentation/docs/track-events#event-properties
    $opt_event_properties: formattedEventFlattened,
  };

  return eventTags as unknown as EventTags;
};

const callOptimizelySdkTrackEvent = (
  eventName: EventName | OptimizelyAllowedEventNames,
  eventTags?: EventTags,
) => {
  debugLog('Optimizely track: ', { eventName, eventTags });

  // @ts-ignore until https://github.com/optimizely/javascript-sdk/pull/957
  optimizelyUser.trackEvent(formatEventName(eventName), eventTags);
};

export const trackEventInOptimizely = (
  eventName: OptimizelyAllowedEventNames,
  eventTags?: EventTags,
) => {
  if (queueUntilOptimizelySetup.ready) {
    callOptimizelySdkTrackEvent(eventName, eventTags);
  } else {
    queueUntilOptimizelySetup.runWhenReady(() =>
      callOptimizelySdkTrackEvent(eventName, eventTags),
    );
  }
};

const trackEventProxy = (event: Event) => {
  const formattedEventName = formatEventName(event.name);

  if (optimizelyAllowedEvents.includes(formattedEventName)) {
    const eventTags = getEventTagsFromEvent(event);

    callOptimizelySdkTrackEvent(formattedEventName as EventName, eventTags);
  }

  trackConversionEvents(event);
};

export const OptimizelyAnalyticsPlugin: Plugin = (event: Event) => {
  if (queueUntilOptimizelySetup.ready) {
    trackEventProxy(event);
  } else {
    queueUntilOptimizelySetup.runWhenReady(() => trackEventProxy(event));
  }
};
